import React from 'react';
import UploadFileIcon from '../../assets/upload-file-icon.svg';
import { Box, Center, Input, Spinner, Text, VStack } from '@chakra-ui/react';
import Dropzone, { FileWithPath } from 'react-dropzone';

export type PreviewMediaType = 'image' | 'video' | string;

export interface DropzoneTwygsProps {
  validateFiles: (files: File[]) => void;
  title?: React.ReactNode;
  desc?: React.ReactNode | string;
  acceptFileTypes?: string;
  isLoading?: boolean;
  isSingle?: boolean;
  notMutiple?: boolean;
  hideContent?: boolean;
  error?: string;
  name?: string;
  label?: string;
  isOptional?: boolean;
  previewMedia?: string | null;
  setPreviewMedia?: (preview: string | null) => void;
  previewMediaType?: PreviewMediaType;
  isMobile?: boolean;
}

const DropzoneTwygsComponent: React.FC<DropzoneTwygsProps & any> = ({
  validateFiles,
  title,
  desc,
  acceptFileTypes,
  isLoading,
  isSingle,
  notMutiple,
  hideContent,
  error,
  name,
  label,
  isOptional,
  previewMedia,
  setPreviewMedia = () => {},
  previewMediaType,
  isMobile,
  ...rest
}) => {
  const handleDrop = (acceptedFiles: FileWithPath[] | File[]) => {
    const file = acceptedFiles[0] as File | undefined;
    if (file) {
      const reader = new FileReader();
      reader.onload = () => {
        setPreviewMedia(String(reader.result));
        validateFiles(Array.from(acceptedFiles) as File[]);
      };
      reader.readAsDataURL(file);
    }
  };

  if (isLoading) {
    return (
      <Center h={'140px'}>
        <Spinner
          thickness="4px"
          speed="0.65s"
          emptyColor="gray.200"
          color="dark-green"
          size={{ base: 'lg', '2xl': 'xl' }}
        />
      </Center>
    );
  }

  return (
    <Dropzone onDrop={handleDrop} multiple={!notMutiple}>
      {({ getRootProps, getInputProps }) => {
        const inputProps = getInputProps() as any;
        inputProps.multiple = true;
        inputProps.accept = acceptFileTypes;
        if (isSingle) inputProps.multiple = false;

        return (
          <VStack
            {...getRootProps()}
            w={'full'}
            backgroundColor="white"
            borderRadius="12px"
            position={'relative'}
            spacing={'1rem'}
            cursor={'pointer'}
            gap={isMobile ? '' : '16px'}
            h={'auto'}
            justifyContent={'center'}
            css={{
              backgroundImage: `url("data:image/svg+xml,%3csvg width='100%25' height='100%25' xmlns='http://www.w3.org/2000/svg'%3e%3crect width='100%25' height='100%25' fill='none' rx='12' ry='12' stroke=${error ? "'red'" : "'%23A6D38D'"} stroke-width='3' stroke-dasharray='6%2c 14' stroke-dashoffset='39' stroke-linecap='square'/%3e%3c/svg%3e")`,
              borderRadius: '12px'
            }}
            minH={'177px'}
            {...rest}
          >
            {previewMedia ? (
              previewMediaType === 'image' ? (
                // eslint-disable-next-line jsx-a11y/img-redundant-alt
                <img
                  src={previewMedia}
                  className="h-[214px] md:h-[360px] max-w-[604px] w-full flex-shrink-0 rounded-[8px] object-cover"
                  alt="Preview"
                />
              ) : (
                <video
                  src={previewMedia}
                  controls={true}
                  className="h-[214px] md:h-[360px] max-w-[604px] w-full flex-shrink-0 object-cover"
                />
              )
            ) : (
              <>
                <img
                  src={UploadFileIcon}
                  alt=""
                  style={{ borderRadius: '50%', scale: isMobile ? '0.8' : '1' }}
                />
                <Box
                  display={hideContent ? 'none' : 'flex'}
                  flexFlow={'column'}
                  gap={isMobile ? '' : '16px'}
                >
                  {title}
                  {desc && typeof desc === 'string' ? (
                    <Text fontWeight="400" color="dim-gray" fontSize="xs" mt={'0.25rem'}>
                      {desc}
                    </Text>
                  ) : (
                    <Box>{desc}</Box>
                  )}
                </Box>
              </>
            )}
            <Input
              accept={acceptFileTypes}
              w="full"
              id="fileInput"
              type="file"
              position="absolute"
              inset={0}
              opacity={0}
              cursor="pointer"
              zIndex={10}
              {...inputProps}
            />
          </VStack>
        );
      }}
    </Dropzone>
  );
};

export default DropzoneTwygsComponent;
